import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import proj4 from "proj4";

import { Button, Stack, TextField, Grid } from "@mui/material";
import M3Base from "../../Base/M3Base";
import TabPanel from "../../../../UI/TabPanel/TabPanel";
import DroneMap from "../../Components/DroneMap/DroneMap";
import OSGridMap from "../../Components/OSGridMap/OSGridMap";
import M3LogsColumns from "../../Components/M3LogsColumns/M3LogsColumns";
import MapGuessHistory from "../../Components/MapGuessHistory/MapGuessHistory";
import CoordinateInput from "../../Components/CoordinatesInput/CoordinatesInput";
import TabsComponent from "../../../../UI/TabsComponent/TabsComponent";

import ErrorBoard from "../../../../UI/ErrorBoard/ErrorBoard";
import { useFeedback } from "../../../../../hooks/useFeedback";
import { useShowReward } from "../../../../../hooks/useShowReward";
import { NumericFormat } from "react-number-format";

import bg from "../../../../../assets/images/m3-bg.png";
import { m3IntermediateR, m3IntermediateD } from "../../../../../data/clueData";

import {
  fetchMission3IntermediateData,
  submitMission3Intermediate,
} from "../../../../../api/MissionApis/mission3Api";
import { pinMultipleClues } from "../../../../../redux/slices/pinclueSlice";
import { getMissionAccomplishmentsThunk } from "../../../../../redux/slices/createUserSlice";

import { getHint } from "../../../../../utils/hint";
import { getInstructionList } from "../../../../../utils/OnScreenInstructions";

import { csvEscape } from "../../../../../helper/csvHelper";
import { forceSyncNow } from "../../../../../helper/forceSync";

import {
  IntermediateStageClearData,
  clueData,
} from "../../constants/intermediateConstants";

import "./M3Intermediate.scss";

const onScreenInstructions = getInstructionList(3, "Intermediate");
const hintList = getHint(3, "Intermediate");
const Assets = [bg, m3IntermediateD, m3IntermediateR];
const STORAGE_KEY = "m3_intermediate_guesses_history_v1";
const GUESS_KEY = "m3_intermediate_guesses_data_v1";

const M3Intermediate = () => {
  const { user } = useSelector((state) => state.createUser);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [alt, setAlt] = useState("");
  const [packets, setPackets] = useState([]);
  const [gpsLogs, setGpsLogs] = useState([]);
  const [markers, setMarkers] = useState([]);
  // const [attempts, setAttempts] = useState(5);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [originalGpsLogs, setOriginalGpsLogs] = useState([]);
  const [selectedPacket, setSelectedPacket] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [updatingMarkerIndex, setUpdatingMarkerIndex] = useState(null);
  const [feedback, setFeedback] = useFeedback(2500);

  const [guessData, setGuessData] = useState(() => {
    // Load from localStorage on first render
    const stored = localStorage.getItem(GUESS_KEY);
    return stored ? JSON.parse(stored) : [];
  });
  const [guessHistory, setGuessHistory] = useState(() => {
    // Load from localStorage on first render
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  });

  const [showReward, setShowReward, clearShowReward] = useShowReward(
    "m3IntermediateShowReward",
    false
  );

  const processMission3Data = (packetLog) => {
    return packetLog
      .filter((packet) => packet.proto === "pos")
      .map((packet) => ({
        timestamp: packet.time,
        coordinates:
          typeof packet.data === "string" && packet.data === "***ENCRYPTED***"
            ? null
            : {
                lat: packet.data.loc_x,
                lng: packet.data.loc_y,
                alt: packet.data.alt || 0,
              },
      }));
  };

  const updateGpsLogsWithCorrectGuesses = () => {
    const correctGuesses = guessData
      .filter((g) => g.correct)
      .sort((a, b) => a.index - b.index);
    const updatedGpsLogs = [...originalGpsLogs];
    let nullIndexCounter = 0;

    correctGuesses.forEach((correctGuess) => {
      const nullIndex = updatedGpsLogs.findIndex(
        (log, index) => index >= nullIndexCounter && log.coordinates === null
      );

      if (nullIndex !== -1) {
        updatedGpsLogs[nullIndex] = {
          ...updatedGpsLogs[nullIndex],
          coordinates: {
            lat: correctGuess.loc_x,
            lng: correctGuess.loc_y,
            alt: correctGuess.alt || 0,
          },
        };
        nullIndexCounter = nullIndex + 1;
      }
    });

    setGpsLogs(updatedGpsLogs);
  };

  const updateMarkersForGuesses = useCallback(() => {
    const allMarkers = guessData
      .map((guess) => {
        if (activeTab === 1) {
          try {
            const OSGB36_TO_WGS84 = proj4("EPSG:27700", "EPSG:4326");
            const [lng, lat] = OSGB36_TO_WGS84.forward([
              guess.loc_x,
              guess.loc_y,
            ]);
            return {
              lat,
              lng,
              alt: guess.alt || 0,
              isCorrect: guess.correct,
              isLocked: guess.correct,
              index: guess.index,
              correct: guess.correct,
            };
          } catch (error) {
            console.error("Error converting coordinates:", error);
            return null;
          }
        }
        return {
          lat: guess.loc_x,
          lng: guess.loc_y,
          alt: guess.alt || 0,
          isCorrect: guess.correct,
          isLocked: guess.correct,
          index: guess.index,
          correct: guess.correct,
        };
      })
      .filter(Boolean);

    setMarkers(allMarkers.sort((a, b) => a.index - b.index));
  }, [guessData, activeTab]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.authToken || !user?.systemName) {
        setFeedback("Please log in to access mission data.", "error");
        return;
      }
      try {
        setLoading(true);
        const data = await fetchMission3IntermediateData({
          authToken: user.authToken,
          systemName: user.systemName,
        });

        // Add packetNumber field to each packet
        const packetsWithNumber = (data.packetLog || []).map((packet, idx) => ({
          ...packet,
          packetNumber: idx + 1,
        }));
        setPackets(packetsWithNumber);
        const processedGpsLogs = processMission3Data(data.packetLog || []);
        setOriginalGpsLogs(processedGpsLogs);
        setGpsLogs(processedGpsLogs);
      } catch (err) {
        console.error("Fetch error:", err);
        setFeedback("Failed to load mission data. Please try again.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.authToken, user?.systemName]);

  useEffect(() => {
    updateGpsLogsWithCorrectGuesses();
    updateMarkersForGuesses();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guessData, activeTab, updateMarkersForGuesses]);

  // NEW: CSV builder + trigger
  const handleDownloadPackets = () => {
    try {
      const fileName = `intermediate_data_${new Date()
        .toISOString()
        .replace(/[:.]/g, "-")}.csv`;

      // Define the CSV columns you want
      const headers = [
        "Packet Number",
        "Time",
        "Src",
        "Dst",
        "Proto",
        "Length",
        "Data",
      ];

      // Build rows
      const lines = [];
      lines.push(headers.join(",")); // header line

      // Assuming "packets" is your in-memory list
      (packets || []).forEach((p) => {
        const row = [
          csvEscape(p.packetNumber),
          csvEscape(p.time),
          csvEscape(p.source),
          csvEscape(p.dest),
          csvEscape(p.proto),
          csvEscape(p.length),
          csvEscape(p.data),
        ];
        lines.push(row.join(","));
      });

      const csv = lines.join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      setDownloadComplete(true);
      setFeedback("Packet CSV downloaded.", "success");

      // (Optional) auto-reset the “done” icon after 2.5s
      setTimeout(() => setDownloadComplete(false), 2500);

      // IMPORTANT: return true so TopRightIcons clears the active class
      return true;
    } catch (e) {
      console.error("Download error:", e);
      setFeedback("Failed to download packet CSV.", "error");
      // still return false so caller can decide (we clear active anyway)
      return false;
    }
  };

  const handlePacketSelect = (selectedPacket) => {
    if (selectedPacket) setSelectedPacket(selectedPacket);
  };

  const getTotalRequiredGuesses = () => {
    return 3 - guessData.filter((g) => g.correct).length;
  };

  const canSubmit = () => {
    const totalGuesses = guessData.length;
    return totalGuesses >= 3;
  };

  const checkLocation = async () => {
    if (
      !markers.length ||
      !gpsLogs.length ||
      !user?.authToken ||
      !user?.systemName ||
      !canSubmit()
    ) {
      setFeedback("Missing markers, GPS logs, or user info.", "info");
      return;
    }

    const guessDataForHistory = guessData.map(({ loc_x, loc_y, alt }) => ({
      lat: loc_x,
      lng: loc_y,
      alt: alt || 0,
    }));

    try {
      const response = await submitMission3Intermediate({
        systemName: user.systemName,
        authToken: user.authToken,
        guesses: guessData.map((g) => ({
          loc_x: g.loc_x,
          loc_y: g.loc_y,
          alt: g.alt || 0,
          index: g.index,
        })),
      });

      let feedbackMessage = "";

      if (Array.isArray(response.feedback)) {
        const updatedGuesses = guessData.map((guess) => {
          const feedbackItem = response.feedback.find(
            (f) => f.index === guess.index
          );
          return {
            ...guess,
            correct: feedbackItem ? feedbackItem.correct : false,
          };
        });

        setGuessData(updatedGuesses);

        const correctCount = updatedGuesses.filter((g) => g.correct).length;
        const totalCount = response.feedback.length;

        if (correctCount === totalCount) {
          feedbackMessage = `All ${totalCount} locations are correct!`;
          setFeedback(feedbackMessage, "success");
          setTimeout(() => setShowReward(true), 2000);
          dispatch(
            getMissionAccomplishmentsThunk({
              systemName: user.systemName,
              authToken: user.authToken,
            })
          );
        } else {
          feedbackMessage = `${correctCount} out of ${totalCount} locations are correct. Please adjust the incorrect ones.`;
          const incorrectIndices = updatedGuesses
            .filter((g) => !g.correct)
            .map((g) => g.index + 1);
          if (incorrectIndices.length > 0) {
            feedbackMessage += ` Incorrect locations: ${incorrectIndices.join(
              ", "
            )}`;
          }
          // setAttempts((prev) => prev - 1);
          setFeedback(feedbackMessage, "error");
        }
      } else {
        feedbackMessage = response?.error || "Unknown error";
        setFeedback(feedbackMessage, "error");
      }

      const newGuess = {
        coordinates: guessDataForHistory,
        success: response?.success || false,
        timestamp: new Date().toLocaleTimeString(),
        feedback: [feedbackMessage],
        detailedFeedback: response.feedback,
      };

      setGuessHistory((prev) => [...prev, newGuess]);
    } catch (err) {
      console.error("Check location error:", err);
      setFeedback("An error occurred. Please try again.", "error");
    } finally {
      dispatch(
        getMissionAccomplishmentsThunk({
          authToken: user.authToken,
          systemName: user.systemName,
        })
      );
    }
  };

  const handleTabChange = (e, newVal) => {
    setActiveTab(newVal);
    setLatitude("");
    setLongitude("");
    setAlt("");
    setUpdatingMarkerIndex(null);
  };

  const onContinue = () => {
    dispatch(
      pinMultipleClues({
        missionId: "mission-3",
        clues: [
          { clueId: "clue-15", level: "intermediate" },
          { clueId: "clue-16", level: "intermediate" },
        ],
      })
    );
    setShowReward(false);
    clearShowReward();
    navigate("/missionComplete", {
      state: { stageClearData: IntermediateStageClearData },
    });
  };

  const handlePinClueBoard = () => {
    dispatch(
      pinMultipleClues({
        missionId: "mission-3",
        clues: [
          { clueId: "clue-15", level: "intermediate" },
          { clueId: "clue-16", level: "intermediate" },
        ],
      })
    );
    navigate("/clueboard");
  };

  const addMarker = () => {
    const requiredGuesses = getTotalRequiredGuesses();

    const getNextIndex = (data) => {
      const used = new Set(data.map((g) => g.index));
      let i = 0;
      while (used.has(i)) i++;
      return i;
    };

    if (activeTab === 1) {
      if (latitude === "" || longitude === "" || alt === "") {
        setFeedback(
          "X coordinate, Y coordinate, and Altitude are required for OS Grid.",
          "info"
        );
        return;
      }

      const x = parseFloat(latitude);
      const y = parseFloat(longitude);
      const altitude = parseFloat(alt);

      if (isNaN(x) || x < 0 || x > 900000) {
        setFeedback(
          "Invalid X coordinate: must be between 0 and 900000",
          "error"
        );
        return;
      }

      if (isNaN(y) || y < 0 || y > 1400000) {
        setFeedback(
          "Invalid Y coordinate: must be between 0 and 1400000",
          "error"
        );
        return;
      }

      if (isNaN(altitude)) {
        setFeedback("Invalid altitude: must be a valid number", "error");
        return;
      }

      if (guessData.length >= requiredGuesses) {
        setFeedback(
          `Maximum ${requiredGuesses} new guesses allowed (${
            guessData.filter((g) => g.correct).length
          } already correct)`,
          "info"
        );
        return;
      }

      try {
        const newIndex = getNextIndex(guessData);

        const newGuessData = {
          loc_x: x,
          loc_y: y,
          alt: altitude,
          index: newIndex,
          correct: null,
        };

        setGuessData((prev) => [...prev, newGuessData]);
        setLatitude("");
        setLongitude("");
        setAlt("");
      } catch (error) {
        console.error("Error converting OS Grid to lat/lng:", error);
        setFeedback(
          "Error converting coordinates. Please check your input.",
          "error"
        );
      }
    } else {
      const lat = parseFloat(latitude);
      const lng = parseFloat(longitude);

      if (isNaN(lat) || lat < -90 || lat > 90) {
        setFeedback("Invalid latitude: must be between -90 and 90", "error");
        return;
      }

      if (isNaN(lng) || lng < -180 || lng > 180) {
        setFeedback("Invalid longitude: must be between -180 and 180", "error");
        return;
      }

      if (guessData.length >= requiredGuesses) {
        setFeedback(
          `Maximum ${requiredGuesses} new guesses allowed (${
            guessData.filter((g) => g.correct).length
          } already correct)`,
          "success"
        );
        return;
      }

      const newIndex = getNextIndex(guessData);

      const newGuessData = {
        loc_x: lat,
        loc_y: lng,
        alt: parseFloat(alt) || 0,
        index: newIndex,
        correct: null,
      };

      setGuessData((prev) => [...prev, newGuessData]);
      setLatitude("");
      setLongitude("");
      setAlt("");
    }
  };

  // const handleOSGridCoordinateSelect = (osGridCoords) => {
  //   setLatitude(osGridCoords.x.toString());
  //   setLongitude(osGridCoords.y.toString());
  // };

  const handleMapClickWithAltitude = (osGridCoords) => {
    setLatitude(osGridCoords.x.toString());
    setLongitude(osGridCoords.y.toString());
  };

  const handleXChange = (values) => {
    setLatitude(values.value);

    if (values.value === "") {
      setFeedback("");
      return;
    }

    if (activeTab === 1) {
      const num = parseFloat(values.value);
      if (num < 0 || num > 900000) {
        setFeedback("X coordinate must be between 0 and 900000.", "error");
      } else {
        setFeedback("");
      }
    } else {
      const num = parseFloat(values.value);
      if (num < -90 || num > 90) {
        setFeedback("Latitude must be between -90 and 90.", "error");
      } else {
        setFeedback("");
      }
    }
  };

  const handleYChange = (values) => {
    setLongitude(values.value);

    if (values.value === "") {
      setFeedback("");
      return;
    }

    if (activeTab === 1) {
      const num = parseFloat(values.value);
      if (num < 0 || num > 1400000) {
        setFeedback("Y coordinate must be between 0 and 1400000.", "error");
      } else {
        setFeedback("");
      }
    } else {
      const num = parseFloat(values.value);
      if (num < -180 || num > 180) {
        setFeedback("Longitude must be between -180 and 180.", "error");
      } else {
        setFeedback("");
      }
    }
  };

  const handleLatChangeFromTab1 = (val) => {
    const num = parseFloat(val);
    setLatitude(val);

    if (val === "") {
      setFeedback("");
      return;
    }

    if (num < -90 || num > 90) {
      setFeedback("Latitude must be between -90 and 90.", "error");
    } else {
      setFeedback("");
    }
  };

  const handleLngChangeFromTab1 = (val) => {
    const num = parseFloat(val);
    setLongitude(val);

    if (val === "") {
      setFeedback("");
      return;
    }

    if (num < -180 || num > 180) {
      setFeedback("Longitude must be between -180 and 180.", "error");
    } else {
      setFeedback("");
    }
  };

  const handleUpdateMarker = (index) => {
    const guess = guessData.find((g) => g.index === index);
    if (guess?.correct) {
      setFeedback(
        "Cannot edit correct guesses. They are locked and shown in green.",
        "info"
      );
      return;
    }
    if (!guess) {
      setFeedback("Unable to find marker data for update.", "error");
      return;
    }

    setSelectedMarker({ type: "user", index });
    setLatitude(guess.loc_x.toString());
    setLongitude(guess.loc_y.toString());
    setAlt(guess.alt ? guess.alt.toString() : "");
    setUpdatingMarkerIndex(index);
    setFeedback(
      `Ready to update incorrect marker ${
        index + 1
      }. Modify coordinates and click Update.`,
      "info"
    );
  };

  const updateGuessAndMarker = (index) => {
    if (!latitude || !longitude) {
      setFeedback(
        "Please provide valid coordinates to update the marker.",
        "error"
      );
      return;
    }

    const guess = guessData.find((g) => g.index === index);
    if (guess?.correct) {
      setFeedback("Cannot update correct guesses. They are locked.", "info");
      return;
    }

    const guessDataIndex = guessData.findIndex((g) => g.index === index);
    if (guessDataIndex === -1) {
      setFeedback("Guess data not found for this marker.", "info");
      return;
    }

    let updatedGuessData = [...guessData];
    let updatedMarkers = [...markers];

    if (activeTab === 1) {
      if (alt === "") {
        setFeedback("Altitude is required for OS Grid updates.", "info");
        return;
      }

      const x = parseFloat(latitude);
      const y = parseFloat(longitude);
      const altitude = parseFloat(alt);

      if (isNaN(x) || x < 0 || x > 900000) {
        setFeedback(
          "Invalid X coordinate: must be between 0 and 900000",
          "error"
        );
        return;
      }

      if (isNaN(y) || y < 0 || y > 1400000) {
        setFeedback(
          "Invalid Y coordinate: must be between 0 and 1400000",
          "error"
        );
        return;
      }

      if (isNaN(altitude)) {
        setFeedback("Invalid altitude: must be a valid number", "error");
        return;
      }

      try {
        const OSGB36_TO_WGS84 = proj4("EPSG:27700", "EPSG:4326");
        const [lng, lat] = OSGB36_TO_WGS84.forward([x, y]);

        updatedGuessData[guessDataIndex] = {
          loc_x: x,
          loc_y: y,
          alt: altitude,
          index,
          correct: null,
        };

        const markerIndex = updatedMarkers.findIndex((m) => m.index === index);
        if (markerIndex !== -1) {
          updatedMarkers[markerIndex] = {
            lat,
            lng,
            alt: altitude,
            isCorrect: false,
            isLocked: false,
            index,
            correct: null,
          };
        } else {
          updatedMarkers.push({
            lat,
            lng,
            alt: altitude,
            isCorrect: false,
            isLocked: false,
            index,
            correct: null,
          });
        }
      } catch (error) {
        console.error("Projection conversion error:", error);
        setFeedback(
          "Error converting coordinates. Please check your input.",
          "error"
        );
        return;
      }
    } else {
      const lat = parseFloat(latitude);
      const lng = parseFloat(longitude);
      const altitude = parseFloat(alt) || 0;

      if (isNaN(lat) || lat < -90 || lat > 90) {
        setFeedback("Invalid latitude: must be between -90 and 90", "error");
        return;
      }

      if (isNaN(lng) || lng < -180 || lng > 180) {
        setFeedback("Invalid longitude: must be between -180 and 180", "error");
        return;
      }

      updatedGuessData[guessDataIndex] = {
        loc_x: lat,
        loc_y: lng,
        alt: altitude,
        index,
        correct: null,
      };

      const markerIndex = updatedMarkers.findIndex((m) => m.index === index);
      if (markerIndex !== -1) {
        updatedMarkers[markerIndex] = {
          lat,
          lng,
          alt: altitude,
          isCorrect: false,
          isLocked: false,
          index,
          correct: null,
        };
      } else {
        updatedMarkers.push({
          lat,
          lng,
          alt: altitude,
          isCorrect: false,
          isLocked: false,
          index,
          correct: null,
        });
      }
    }

    setGuessData(updatedGuessData);
    setMarkers(updatedMarkers);
    setLatitude("");
    setLongitude("");
    setAlt("");
    setUpdatingMarkerIndex(null);
    setFeedback(`Incorrect marker ${index + 1} updated successfully.`, "error");
  };

  const handleDeleteMarker = (index) => {
    const guess = guessData.find((g) => g.index === index);
    if (guess?.correct) {
      setFeedback(
        "Cannot delete correct guesses. They are locked and shown in green.",
        "info"
      );
      return;
    }

    const updatedGuessData = guessData.filter((g) => g.index !== index);
    setGuessData(updatedGuessData);
    setFeedback(`Incorrect marker ${index + 1} deleted.`, "info");
  };

  const handleAddOrUpdateMarker = () => {
    if (updatingMarkerIndex !== null) {
      updateGuessAndMarker(updatingMarkerIndex);
    } else {
      addMarker();
    }
  };

  const getButtonConfig = () => {
    if (updatingMarkerIndex !== null) {
      return {
        text: "Update",
        disabled: false,
        variant: "contained",
      };
    } else {
      return {
        text: "Add",
        disabled: guessData.length >= getTotalRequiredGuesses(),
        variant: "contained",
      };
    }
  };

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(guessHistory));
  }, [guessHistory]);

  useEffect(() => {
    localStorage.setItem(GUESS_KEY, JSON.stringify(guessData));
  }, [guessData]);

  const handleSaveMission = () => {
    forceSyncNow("m3-intermediate-manual-save");
    localStorage.setItem(STORAGE_KEY, JSON.stringify(guessHistory));
    localStorage.setItem(GUESS_KEY, JSON.stringify(guessData));
    setFeedback("Mission progress saved.", "success");
  };

  return (
    <M3Base
      loading={loading}
      assets={Assets}
      clueData={clueData}
      hintList={hintList}
      isShowDownload={true}
      isShowCalculator={true}
      isShowStickyNote={true}
      showReward={showReward}
      challengeName="Intermediate"
      handleContinue={onContinue}
      handlePinClue={handlePinClueBoard}
      onDownload={handleDownloadPackets}
      Instructions={onScreenInstructions}
      handleSaveMission={handleSaveMission}
      isDownloadComplete={downloadComplete}
    >
      <Stack useFlexGap gap={2} marginTop={"10px"}>
        <Stack>
          <ErrorBoard
            message={feedback.message}
            severity={feedback.severity}
            visible={!!feedback.message}
          />
        </Stack>

        <Stack className="mission3-intermediate" useFlexGap gap={2}>
          <TabsComponent
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            tabs={["Map", "OS Grid", "Guess History"]}
          />
          <TabPanel value={activeTab} index={0} style={{ flex: 1 }}>
            <Stack direction="row" useFlexGap gap={2}>
              <Stack width="100%" useFlexGap gap={2}>
                <Stack direction="row" spacing={1} width="100%">
                  <CoordinateInput
                    onChange={handleLatChangeFromTab1}
                    value={latitude}
                    label="Latitude"
                    placeholder="00.0000"
                    min={-90}
                    max={90}
                  />
                  <CoordinateInput
                    value={longitude}
                    label="Longitude"
                    placeholder="00.0000"
                    min={-180}
                    max={180}
                    onChange={handleLngChangeFromTab1}
                  />
                  <Stack className="btn-wrapper">
                    <Button
                      variant="contained"
                      onClick={addMarker}
                      className="btn button"
                      disabled={guessData.length >= getTotalRequiredGuesses()}
                    >
                      Add
                    </Button>
                  </Stack>
                </Stack>
                <DroneMap
                  marker={markers}
                  setMarker={setMarkers}
                  // flag="multiple"
                  // lat={51.5485}
                  // lng={-2.5642}
                  // gpsLogs={gpsLogs}
                  // level="intermediate"
                />
              </Stack>
              <M3LogsColumns
                packets={packets}
                GpsLogs={gpsLogs}
                checkLocation={checkLocation}
                isDisable={!canSubmit()}
                selectedPacket={selectedPacket}
                handlePacketSelect={handlePacketSelect}
                setMarkers={setMarkers}
                guessData={guessData}
                handleUpdateMarker={handleUpdateMarker}
                handleDeleteMarker={handleDeleteMarker}
              />
            </Stack>
          </TabPanel>
          <TabPanel value={activeTab} index={1} style={{ flex: 1 }}>
            <Stack useFlexGap gap={2} direction="row">
              <Stack useFlexGap gap={2} width="40%">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <NumericFormat
                      label="X"
                      size="medium"
                      value={latitude}
                      decimalScale={6}
                      autoComplete="off"
                      allowNegative={false}
                      customInput={TextField}
                      onValueChange={handleXChange}
                      slotProps={{
                        htmlInput: { maxLength: 6 },
                      }}
                      allowLeadingZeros={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <NumericFormat
                      label="Y"
                      size="medium"
                      decimalScale={6}
                      value={longitude}
                      autoComplete="off"
                      allowNegative={false}
                      customInput={TextField}
                      onValueChange={handleYChange}
                      slotProps={{
                        htmlInput: { maxLength: 6 },
                      }}
                      allowLeadingZeros={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <NumericFormat
                      fullWidth
                      value={alt}
                      label="Alt"
                      size="medium"
                      allowNegative={false}
                      customInput={TextField}
                      inputProps={{
                        maxLength: 5,
                      }}
                      onValueChange={(values) => setAlt(values.value)}
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3} className="btn-wrapper">
                    <Button
                      variant={getButtonConfig().variant}
                      onClick={handleAddOrUpdateMarker}
                      disabled={getButtonConfig().disabled}
                      className={`btn button ${
                        getButtonConfig().disabled ? "disabled" : ""
                      }`}
                    >
                      {getButtonConfig().text}
                    </Button>
                  </Grid>
                </Grid>
                <OSGridMap
                  lat={51.5485}
                  lng={-2.5642}
                  flag="multiple"
                  marker={markers}
                  gpsLogs={gpsLogs}
                  level="intermediate"
                  blinkDuration={5000}
                  setMarker={setMarkers}
                  setGuessData={setGuessData}
                  selectedMarker={selectedMarker}
                  onCoordinateSelect={handleMapClickWithAltitude}
                />
              </Stack>
              <Stack width={"60%"}>
                <M3LogsColumns
                  packets={packets}
                  GpsLogs={gpsLogs}
                  guessData={guessData}
                  setMarkers={setMarkers}
                  isDisable={!canSubmit()}
                  checkLocation={checkLocation}
                  selectedPacket={selectedPacket}
                  handlePacketSelect={handlePacketSelect}
                  handleUpdateMarker={handleUpdateMarker}
                  handleDeleteMarker={handleDeleteMarker}
                />
              </Stack>
            </Stack>
          </TabPanel>
          <TabPanel value={activeTab} index={2} style={{ flex: 1 }}>
            <MapGuessHistory guessHistory={guessHistory} height="400" />
          </TabPanel>
        </Stack>
      </Stack>
    </M3Base>
  );
};

export default M3Intermediate;
